import { assert } from "@/core";
import { BaseService } from "./base.service";
import { ResponseWrapper } from "./util";

export class AppointmentTypeService extends BaseService {
  static get entity() {
    return "appointment-type";
  }

  /**
   * Get all appointment types
   * @param {Object} parameters - Query parameters
   * @returns {Promise<ResponseWrapper>}
   */
  static async getAll(parameters = {}) {
    assert.object(parameters);

    const params = { ...parameters };

    const response = await this.request({ auth: true }).get(
      `${this.entity}`,
      { params }
    );

    // ✅ Normalize API response
    // Supports:
    // { data: { data: [...] } }
    // { data: [...] }
    // { success, data: { data: [...] } }
    // const list =
    //   response?.data?.data?.data ??
    //   response?.data?.data ??
    //   [];

    return new ResponseWrapper(response, response);
  }

  /**
   * Get appointment type by ID
   * @param {string} id
   * @returns {Promise<ResponseWrapper>}
   */
  static async getById(id) {
    assert.string(id, { required: true });

    const response = await this.request({ auth: true }).get(
      `${this.entity}/${id}`
    );

    return new ResponseWrapper(response, response?.data?.data);
  }

  /**
   * Create a new appointment type
   * @param {Object} data
   * @returns {Promise<ResponseWrapper>}
   */
  static async create(data = {}) {
    assert.object(data, { required: true });

    const response = await this.request({ auth: true }).post(
      `${this.entity}`,
      data
    );

    return new ResponseWrapper(response, response);
  }

  /**
   * Update an appointment type
   * @param {string} id
   * @param {Object} data
   * @returns {Promise<ResponseWrapper>}
   */
  static async update(id, data = {}) {
    assert.string(id, { required: true });
    assert.object(data, { required: true });

    const response = await this.request({ auth: true }).patch(
      `${this.entity}/${id}`,
      data
    );

    return new ResponseWrapper(response, response);
  }

  /**
   * Delete an appointment type
   * @param {string} id
   * @returns {Promise<ResponseWrapper>}
   */
  static async delete(id) {
    assert.string(id, { required: true });

    const response = await this.request({ auth: true }).delete(
      `${this.entity}/${id}`
    );

    return new ResponseWrapper(response, response);
  }

  /**
   * Get appointment types with filtering
   * @param {Object} filters
   * @returns {Promise<ResponseWrapper>}
   */
  static async getFiltered(filters = {}) {
    assert.object(filters);

    const params = { ...filters };

    const response = await this.request({ auth: true }).get(
      `${this.entity}/filter`,
      { params }
    );

    return new ResponseWrapper(response, response?.data?.data);
  }

  /**
   * Update appointment type status
   * @param {string} id
   * @param {boolean} isActive
   * @returns {Promise<ResponseWrapper>}
   */
  static async updateStatus(id, isActive) {
    assert.string(id, { required: true });
    assert.boolean(isActive, { required: true });

    const response = await this.request({ auth: true }).patch(
      `${this.entity}/${id}/status`,
      { isActive }
    );

    return new ResponseWrapper(response, response?.data?.data);
  }
}
